import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableCell } from "~/components/TableView/TableCell";
import { TABLE_CELL_V_PADDING, TABLE_ROW_HEIGHT } from "~/components/TableView/ui";
import { usePopperContainer } from "~/hooks/usePopperContainer";
const EDITING_CLEARANCE = 100;
export const EditableTableCell = /*#__PURE__*/ memo((param)=>{
    let { columnWidth, renderValue, renderEditor, tableCellStyle, additionalPopperOffset = 0, ["data-testid"]: testId } = param;
    const tableCellRef = useRef(null);
    const popperOptions = useMemo(()=>({
            placement: "bottom-start",
            modifiers: [
                {
                    name: "offset",
                    options: {
                        offset: [
                            0,
                            -(TABLE_ROW_HEIGHT - TABLE_CELL_V_PADDING * 2) + additionalPopperOffset
                        ]
                    }
                },
                {
                    name: "preventOverflow",
                    options: {
                        boundary: "viewport",
                        padding: TABLE_CELL_V_PADDING
                    }
                }
            ]
        }), [
        additionalPopperOffset
    ]);
    const { popperInstance, PopperContainer } = usePopperContainer({
        containerRef: tableCellRef,
        options: popperOptions,
        usePortal: true
    });
    const [isOpen, setOpen] = useState(false);
    const openEditor = useCallback((event)=>{
        event === null || event === void 0 ? void 0 : event.stopPropagation();
        if (!renderEditor) return;
        setOpen(true);
    }, [
        renderEditor,
        setOpen
    ]);
    const closeEditor = useCallback((event)=>{
        event === null || event === void 0 ? void 0 : event.stopPropagation();
        if (!renderEditor) return;
        setOpen(false);
    }, [
        renderEditor,
        setOpen
    ]);
    useEffect(()=>{
        if (!isOpen && popperInstance.current) {
            popperInstance.current.destroy();
            popperInstance.current = null;
        }
    }, [
        isOpen,
        popperInstance
    ]);
    const editor = useMemo(()=>renderEditor && isOpen ? /*#__PURE__*/ _jsx(PopperContainer, {
            style: {
                width: columnWidth + EDITING_CLEARANCE
            },
            children: renderEditor(closeEditor)
        }) : null, [
        PopperContainer,
        renderEditor,
        isOpen,
        columnWidth,
        closeEditor
    ]);
    const value = useMemo(()=>renderValue ? renderValue(openEditor) : null, [
        renderValue,
        openEditor
    ]);
    const editButton = useMemo(()=>renderEditor ? /*#__PURE__*/ _jsx(Button, {
            appearance: "ghost",
            color: "grey",
            className: "opacity-0 group-hover/editable-table-cell:opacity-100",
            "data-testid": testId,
            onClick: openEditor,
            // Prevent item selection when clicking on this button
            onMouseDown: (event)=>event.stopPropagation(),
            size: "medium",
            children: "Edit"
        }) : null, [
        renderEditor,
        openEditor,
        testId
    ]);
    const handleMouseDown = useCallback((e)=>{
        // Allow clicks on the table cell itself to select the row
        // Prevent clicks related to editor components from selecting the row
        if (e.target === tableCellRef.current) return;
        // Allow text selection in input and textarea elements
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
            e.stopPropagation();
            return;
        }
        e.preventDefault();
    }, [
        tableCellRef
    ]);
    const handleDoubleClick = useCallback((e)=>{
        // Allow clicks on the table cell itself to select the row
        // Prevent clicks related to editor components from selecting the row
        if (e.target === tableCellRef.current) return;
        // Always prevent the double-click from propagating to prevent row selection
        e.stopPropagation();
        e.preventDefault();
    }, [
        tableCellRef
    ]);
    return /*#__PURE__*/ _jsxs(TableCell, {
        ref: tableCellRef,
        style: tableCellStyle,
        className: "group/editable-table-cell",
        // Prevent table row selection when clicking to open the cell editor
        onMouseDown: handleMouseDown,
        // TableRows have a double click behavior that we want to prevent here in
        // order to provide a positive UX when interacting with the editor components
        onDoubleClick: handleDoubleClick,
        children: [
            editor,
            value || editButton
        ]
    });
});
EditableTableCell.displayName = "EditableTableCell";
